import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import asyncErrorHandler from 'utils/asyncErrorHandler';
import timeTracking from 'services/timeTracking';
import { RootState } from 'store';
import {
  addActiveLogData,
  getActiveLogsSilent,
  updateActiveLogData,
} from 'store/container/activeLogs/activeLogs-slice';

const useTaskStartStop = (taskId: string) => {
  const dispatch = useDispatch();
  const { data: dataLogs } = useSelector((store: RootState) => store.activeLogs);
  const [saving, setSaving] = useState(false);

  const current = useMemo(() => {
    return dataLogs?.find((x) => x.source === 'task' && x.source_uuid === taskId) ?? null;
  }, [dataLogs, taskId]);

  const isRunning = current?.status === 'running';
  const hasPausedLog = !!current && current.status === 'paused';

  const toggle = async (e?: React.MouseEvent) => {
    e?.stopPropagation();
    e?.preventDefault();
    if (saving) return;

    setSaving(true);

    try {
      if (isRunning && current) {
        const timer = await timeTracking.pauseTimer(current.uuid);
        dispatch(updateActiveLogData(timer));
      } else {
        const timer =
          current?.status === 'paused'
            ? await timeTracking.resumeTimer(current.uuid)
            : await timeTracking.startTimer({ source: 'task', sourceUuid: taskId });

        const exists = dataLogs?.some((x) => x.uuid === timer.uuid);
        dispatch(exists ? updateActiveLogData(timer) : addActiveLogData(timer));

        dispatch(getActiveLogsSilent());
      }
    } catch (error) {
      asyncErrorHandler(error);
    } finally {
      setSaving(false);
    }
  };

  return { isRunning, hasPausedLog, current, saving, toggle };
};

export default useTaskStartStop;
